import { api } from './axios';

export interface Designation {
  id: number;
  name: string;
  departmentId: number | null;
  departmentName: string | null;
  active: boolean;
}

export interface DesignationInput {
  name: string;
  departmentId?: number | null;
}

export interface StaffSummary {
  id: number;
  firstName: string;
  lastName: string;
  cnic: string | null;
  phone: string | null;
  designationId: number | null;
  designationName: string | null;
  departmentId: number | null;
  departmentName: string | null;
  employmentTypeId: number;
  employmentTypeName: string | null;
  joiningDate: string;
  active: boolean;
  leavingDate: string | null;
  linkedUserId: number | null;
  linkedUsername: string | null;
  linkedUserActive: boolean | null;
}

export interface StaffDetail {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  genderId: number | null;
  genderName: string | null;
  photoPath: string | null;
  cnic: string | null;
  addressLine: string | null;
  cityId: number | null;
  cityName: string | null;
  phone: string | null;
  designationId: number | null;
  designationName: string | null;
  departmentId: number | null;
  departmentName: string | null;
  employmentTypeId: number;
  employmentTypeName: string | null;
  joiningDate: string;
  leavingDate: string | null;
  leavingReason: string | null;
  active: boolean;
  bankName: string | null;
  bankAccountNumber: string | null;
  bankBranch: string | null;
  iban: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  emergencyRelation: string | null;
  createdAt: string;
  qualifications: Qualification[];
  linkedUserId: number | null;
  linkedUsername: string | null;
  linkedUserActive: boolean | null;
}

export interface StaffInput {
  firstName: string;
  lastName: string;
  dateOfBirth?: string | null;
  genderId?: number | null;
  photoPath?: string | null;
  cnic?: string;
  addressLine?: string;
  cityId?: number | null;
  phone?: string;
  designationId?: number | null;
  departmentId?: number | null;
  employmentTypeId: number;
  joiningDate: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankBranch?: string;
  iban?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyRelation?: string;
}

export interface Qualification {
  id: number;
  staffId: number;
  degreeTitle: string;
  institution: string;
  yearCompleted: number | null;
  grade: string | null;
  experience: boolean;
  orgName: string | null;
  fromDate: string | null;
  toDate: string | null;
}

export interface QualificationInput {
  degreeTitle: string;
  institution: string;
  yearCompleted?: number | null;
  grade?: string;
  experience: boolean;
  orgName?: string;
  fromDate?: string | null;
  toDate?: string | null;
}

export interface StaffPage {
  rows: StaffSummary[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

interface SpringPage<T> {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export const hrApi = {
  // designations
  listDesignations: (activeOnly = true, departmentId?: number) =>
    api
      .get<Designation[]>('/hr/designations', { params: { activeOnly, departmentId } })
      .then((r) => r.data),
  createDesignation: (body: DesignationInput) =>
    api.post<Designation>('/hr/designations', body).then((r) => r.data),
  updateDesignation: (id: number, body: DesignationInput) =>
    api.put<Designation>(`/hr/designations/${id}`, body).then((r) => r.data),
  toggleDesignation: (id: number, value: boolean) =>
    api
      .patch<Designation>(`/hr/designations/${id}/active`, null, { params: { value } })
      .then((r) => r.data),

  // staff
  searchStaff: (params: {
    q?: string;
    designationId?: number;
    departmentId?: number;
    employmentTypeId?: number;
    active?: boolean;
    page?: number;
    size?: number;
  }): Promise<StaffPage> =>
    api
      .get<SpringPage<StaffSummary>>('/hr/staff', {
        params: {
          q: params.q || undefined,
          designationId: params.designationId,
          departmentId: params.departmentId,
          employmentTypeId: params.employmentTypeId,
          active: params.active,
          page: params.page ?? 0,
          size: params.size ?? 25,
        },
      })
      .then((r) => ({
        rows: r.data.content,
        page: r.data.number,
        size: r.data.size,
        totalElements: r.data.totalElements,
        totalPages: r.data.totalPages,
      })),
  getStaff: (id: number) => api.get<StaffDetail>(`/hr/staff/${id}`).then((r) => r.data),
  createStaff: (body: StaffInput) => api.post<StaffDetail>('/hr/staff', body).then((r) => r.data),
  updateStaff: (id: number, body: StaffInput) =>
    api.put<StaffDetail>(`/hr/staff/${id}`, body).then((r) => r.data),
  terminateStaff: (id: number, body: { leavingDate: string; reason: string }) =>
    api.post<StaffDetail>(`/hr/staff/${id}/terminate`, body).then((r) => r.data),
  reactivateStaff: (id: number) =>
    api.post<StaffDetail>(`/hr/staff/${id}/reactivate`).then((r) => r.data),

  // qualifications
  listQualifications: (id: number) =>
    api.get<Qualification[]>(`/hr/staff/${id}/qualifications`).then((r) => r.data),
  addQualification: (id: number, body: QualificationInput) =>
    api.post<Qualification>(`/hr/staff/${id}/qualifications`, body).then((r) => r.data),
  updateQualification: (id: number, qid: number, body: QualificationInput) =>
    api.put<Qualification>(`/hr/staff/${id}/qualifications/${qid}`, body).then((r) => r.data),
  deleteQualification: (id: number, qid: number) =>
    api.delete<void>(`/hr/staff/${id}/qualifications/${qid}`).then((r) => r.data),
};
